/**
 * PrescriptionDetailSheet Component
 *
 * A comprehensive sliding sheet/drawer that displays all details of a prescription.
 * This is the primary view for practitioners to review and manage prescriptions.
 *
 * Features:
 * - Current status display with workflow progress visualization
 * - Patient and practitioner information
 * - Medication details (expandable accordion)
 * - Shipping information (carrier, service, cost, free shipment status)
 * - Cancellation request status and history
 * - Audit trail for tracking changes
 * - Action buttons for verification, payment, and pharmacy submission
 *
 * Shipping Information Display:
 * The component displays shipping details if they have been configured:
 * - Shows carrier name (e.g., "UPS", "Ameriship")
 * - Shows service type (e.g., "Ground", "Priority Mail")
 * - Shows shipping cost with free shipment handling:
 *   - If free shipment: shows original cost with strikethrough + $0.00
 *   - If paid shipment: shows the actual cost
 * - Displays "Free Shipping Applied" badge when applicable
 *
 * @module PrescriptionDetailSheet
 */

import React, { useState, useEffect } from 'react';
import { getDefaultContactValue } from '@/lib/utils';
import { Sheet, SheetContent, SheetClose, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  X,
  Calendar,
  User,
  Pill,
  CreditCard,
  Package,
  Truck,
  CheckCircle,
  Clock,
  Send,
  PackageCheck,
  AlertCircle,
  Syringe,
  RotateCcw,
  Hash,
  Scale,
  CalendarDays,
  Palette,
  FileText,
  MessageSquare,
  FlaskConical,
  Info,
  Loader2,
  Flag,
  XCircle,
  Ban,
  Gift,
  DollarSign,
  Layers,
  Pencil,
} from 'lucide-react';
import { Prescription, PrescriptionAuditLog, PrescriptionCancellationRequest } from '@/lib/types';
import { medicationUnits } from '@/lib/medication-unit';
import { formatToLocalDate } from '@/lib/utils';
import VerificationConfirmationDialog from '@/components/dialog/verification-confirmation-dialog';
import PaymentSheet from './PaymentSheet';
import SendPaymentLinkDialog from '../dialog/SendPaymentLinkDialog';
import RefundDialog from './RefundDialog';
import CancelPrescriptionDialog from './Prescriptions/CancelPrescriptionDialog';
import { prescriptionService } from '@/services/prescriptionService';
import { useClinic } from '@/contexts/ClinicContext';
import { toast } from '@/hooks/use-toast';
import { permissionCheck } from '@/lib/permission-check';
import PrescriptionAuditTrail from './PrescriptionAuditTrail';
import { clinicPaymentService } from '@/services/clinicPaymentService';
import { useGuidedOrderByPrescription } from '@/hooks/useGuidedOrders';
import { OrderLifecycleTracker } from '@/components/guided-order/OrderLifecycleTracker';
import { GuidedOrderDetailSheet } from '@/components/guided-order/GuidedOrderDetailSheet';
import EditMedicationsDialog from './EditMedicationsDialog';
import PrescriptionFormSheet from './PrescriptionFormSheet';

/**
 * Props for the PrescriptionDetailSheet component
 */
interface PrescriptionDetailSheetProps {
  /** Whether the sheet is open/visible */
  open: boolean;
  /** Callback when sheet open state changes */
  onOpenChange: (open: boolean) => void;
  /** The prescription object to display */
  prescription: Prescription;
  /** Callback when prescription status is updated */
  onStatusUpdate: (prescriptionId: string, status: Prescription['status']) => void;
  /** Optional callback when any action is completed (for list refresh) */
  onActionComplete?: () => void;
}

const PrescriptionDetailSheet: React.FC<PrescriptionDetailSheetProps> = ({
  open,
  onOpenChange,
  prescription,
  onStatusUpdate,
  onActionComplete,
}) => {
  const { isPractitioner, isAdmin, rolePermissions, clinic } = useClinic();
  const [isVerificationConfirmationOpen, setIsVerificationConfirmationOpen] = useState(false);
  const [isPaymentSheetOpen, setIsPaymentSheetOpen] = useState(false);
  const [isSendLinkSheetOpen, setIsSendLinkSheetOpen] = useState(false);
  const [isRefundDialogOpen, setIsRefundDialogOpen] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isEditMedicationsOpen, setIsEditMedicationsOpen] = useState(false);
  const [isEditPrescriptionOpen, setIsEditPrescriptionOpen] = useState(false);
  const [isVerificationProcessing, setIsVerificationProcessing] = useState(false);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [isSubmittingToPharmacy, setIsSubmittingToPharmacy] = useState(false);
  const [auditLogs, setAuditLogs] = useState<PrescriptionAuditLog[]>([]);
  const [isLoadingAuditTrail, setIsLoadingAuditTrail] = useState(false);
  const [transactionId, setTransactionId] = useState<string | null>(null);
  const [transactionAmount, setTransactionAmount] = useState<number>(0);
  const [guidedOrderDetailOpen, setGuidedOrderDetailOpen] = useState(false);
  const [guidedOrderIdToView, setGuidedOrderIdToView] = useState<string | null>(null);

  const guidedOrderId = prescription?.guided_order_id;
  const prescriptionId = guidedOrderId && prescription?.id != null ? String(prescription.id) : undefined;
  const { data: guidedOrderResponse } = useGuidedOrderByPrescription(prescriptionId);
  const guidedOrder = guidedOrderResponse?.data;
  const [fullPrescription, setFullPrescription] = useState<Prescription | null>(null);
  const [isLoadingPrescription, setIsLoadingPrescription] = useState(false);
  const [prescriptionTotalAmountCents, setPrescriptionTotalAmountCents] = useState<number | null>(null);
  const [totalDiscountCents, setTotalDiscountCents] = useState<number>(0);
  const [subtotalBeforeDiscountCents, setSubtotalBeforeDiscountCents] = useState<number>(0);
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-amber-500 hover:bg-amber-500 text-white border-0';
      case 'verified':
        return 'bg-brand-teal hover:bg-brand-teal text-white border-0';
      case 'paid':
        return 'bg-emerald-600 hover:bg-emerald-600 text-white border-0';
      case 'sent_to_pharmacy':
        return 'bg-brand-copper hover:bg-brand-copper text-white border-0';
      case 'acknowledged':
        return 'bg-indigo-500 hover:bg-indigo-500 text-white border-0';
      case 'filled':
        return 'bg-orange-500 hover:bg-orange-500 text-white border-0';
      case 'shipped':
        return 'bg-sky-500 hover:bg-sky-500 text-white border-0';
      case 'delivered':
        return 'bg-emerald-600 hover:bg-emerald-600 text-white border-0';
      case 'ready':
        return 'bg-cyan-500 hover:bg-cyan-500 text-white border-0';
      default:
        return 'bg-slate-500 hover:bg-slate-500 text-white border-0';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Pending Review';
      case 'verified':
        return 'Verified';
      case 'paid':
        return 'Paid';
      case 'sent_to_pharmacy':
        return 'Sent to Pharmacy';
      case 'acknowledged':
        return 'Acknowledged';
      case 'filled':
        return 'Being Filled';
      case 'shipped':
        return 'Shipped';
      case 'delivered':
        return 'Delivered';
      case 'ready':
        return 'Ready for Shipment';
      default:
        return status;
    }
  };

  const getPriorityText = (priority: number) => {
    switch (priority) {
      case -1:
        return 'Low Precedence';
      case 1:
        return 'Normal';
      case 2:
        return 'Elevated';
      case 3:
        return 'Urgent';
      default:
        return 'Normal';
    }
  };

  const getPriorityColor = (priority: number) => {
    switch (priority) {
      case -1:
        return 'bg-slate-100 text-slate-600';
      case 1:
        return 'bg-green-100 text-green-700';
      case 2:
        return 'bg-amber-100 text-amber-700';
      case 3:
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-green-100 text-green-700';
    }
  };

  // Fetch full prescription details to ensure all fields (including follow-up lab) are included
  const fetchFullPrescription = async () => {
    if (!prescription?.id || !clinic?.id) return;
    try {
      setIsLoadingPrescription(true);
      const response = await prescriptionService.getPrescriptionById(clinic.id, prescription.id);
      if (response?.success && response?.data?.prescription) {
        setFullPrescription(response.data.prescription);
        if (response.data.total_amount != null) {
          setPrescriptionTotalAmountCents(response.data.total_amount);
        }
        setTotalDiscountCents(response.data.total_discount_cents || 0);
        setSubtotalBeforeDiscountCents(response.data.subtotal_before_discount || 0);
      } else {
        // Fallback to prop if API fails
        setFullPrescription(prescription);
      }
    } catch (error) {
      console.error('Error fetching full prescription:', error);
      // Fallback to prop if API fails
      setFullPrescription(prescription);
    } finally {
      setIsLoadingPrescription(false);
    }
  };

  // Fetch when sheet opens
  useEffect(() => {
    if (open && prescription?.id && clinic?.id) {
      fetchFullPrescription();
    } else if (open && prescription) {
      // If no clinic ID, use the prescription prop directly
      setFullPrescription(prescription);
    } else if (!open) {
      // Reset when sheet closes
      setFullPrescription(null);
    }
  }, [open, prescription?.id, clinic?.id]);

  // Fetch audit trail when sheet opens
  useEffect(() => {
    if (open && prescription?.id) {
      const fetchAuditTrail = async () => {
        try {
          setIsLoadingAuditTrail(true);
          const response = await prescriptionService.getPrescriptionAuditTrail(prescription.id);
          if (response?.success) {
            setAuditLogs(response.data || []);
          }
        } catch (error) {
          console.error('Error fetching audit trail:', error);
          setAuditLogs([]);
        } finally {
          setIsLoadingAuditTrail(false);
        }
      };
      fetchAuditTrail();
    }
  }, [open, prescription?.id]);

  const getWorkflowSteps = (status: string) => {
    const steps = [
      { key: 'pending', label: 'Created', icon: Clock },
      { key: 'verified', label: 'Verified', icon: CheckCircle },
      { key: 'paid', label: 'Paid', icon: CreditCard },
      { key: 'sent_to_pharmacy', label: 'Sent to Pharmacy', icon: Send },
      { key: 'acknowledged', label: 'Acknowledged', icon: CheckCircle },
      { key: 'filled', label: 'Filled', icon: Package },
      { key: 'ready', label: 'Ready for Shipment', icon: PackageCheck },
      { key: 'shipped', label: 'Shipped', icon: Truck },
      { key: 'delivered', label: 'Delivered', icon: PackageCheck },
    ];

    const currentIndex = steps.findIndex(step => step.key === status);
    const isPaid = displayPrescription.payment_status === 'paid';

    return steps.map((step, index) => {
      if (step.key === 'paid' && isPaid) {
        const is_current = status === 'pending' || status === 'verified';
        return {
          ...step,
          completed: true,
          current: is_current,
        };
      }
      return {
        ...step,
        completed: index <= currentIndex,
        current:
          index === currentIndex && (!isPaid || !(status === 'pending' || status === 'verified')),
      };
    });
  };

  const handleVerifyClick = () => {
    setIsVerificationConfirmationOpen(true);
  };

  const handlePaymentClick = () => {
    setIsPaymentSheetOpen(true);
  };

  const handleSendLinkClick = () => {
    setIsSendLinkSheetOpen(true);
  };

  const handleSubmitToPharmacy = async () => {
    try {
      setIsSubmittingToPharmacy(true);
      const result = await prescriptionService.submitPrescription(prescription.id);
      if ((result as any).isExternalFulfillment) {
        toast({
          variant: 'success',
          title: 'Queued for external fulfillment',
          description: 'The order has been queued for external pharmacy fulfillment.',
        });
      } else {
        toast({
          variant: 'success',
          title: 'Order submitted to pharmacy successfully',
          description: 'The order has been submitted to the pharmacy',
        });
      }
      onActionComplete?.();
      onStatusUpdate(prescription.id, (result as any).isExternalFulfillment ? 'external_fulfillment' : 'sent_to_pharmacy');
    } catch (error: any) {
      console.error('Error submitting prescription to pharmacy:', error);
      const errorMessage =
        error?.message ??
        error?.response?.data?.error?.message ??
        error?.response?.data?.message ??
        'Please try again later';
      toast({
        variant: 'destructive',
        title: 'Order submission failed',
        description: errorMessage,
      });
    } finally {
      setIsSubmittingToPharmacy(false);
    }
  };

  // Use full prescription if available, otherwise fall back to prop
  const displayPrescription = fullPrescription || prescription;
  const latestLink = displayPrescription.latest_prescription_link;

  const email = getDefaultContactValue(displayPrescription.patient?.contacts, 'email');

  // Show loading state while fetching full prescription
  if (isLoadingPrescription) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className='flex w-full !max-w-[640px] flex-col gap-0 rounded-l-2xl p-0 overflow-hidden [&>button]:hidden'>
          <div className='flex items-center justify-center h-full'>
            <Loader2 className='h-8 w-8 animate-spin text-purple-600' />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className='flex w-full !max-w-[640px] flex-col gap-0 rounded-l-2xl p-0 overflow-hidden [&>button]:hidden'>
        {/* Header */}
        <div className='flex items-center justify-between border-b border-slate-200 px-6 py-4 flex-shrink-0'>
          <SheetTitle className='font-semibold text-slate-900 min-w-0 flex-1' style={{ fontSize: '1.05rem' }}>
            Order Details
          </SheetTitle>
          <SheetClose asChild>
            <button className='flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border border-slate-200 transition-colors hover:bg-slate-50'>
              <X className='h-5 w-5 text-slate-600' />
            </button>
          </SheetClose>
        </div>

        {/* Content */}
        <div className='flex-1 overflow-y-auto px-6 py-6 min-w-0'>
          <div className='space-y-8 min-w-0'>
            {/* Current Status */}
            <div className='space-y-4'>
              <h3 className='text-[0.8rem] font-semibold uppercase text-[#60378B]'>
                Current Status
              </h3>
              <div className='rounded-lg border border-slate-200 p-4'>
                <div className='flex items-center space-x-4'>
                  <div
                    className={`flex h-12 w-12 items-center justify-center rounded-xl ${
                      displayPrescription.is_external_fulfillment && displayPrescription.status !== 'pending' && displayPrescription.status !== 'verified'
                        ? displayPrescription.external_fulfillment_status === 'sent'
                          ? 'bg-teal-100 text-teal-600'
                          : displayPrescription.external_fulfillment_status === 'shipped'
                          ? 'bg-sky-100 text-sky-600'
                          : displayPrescription.external_fulfillment_status === 'delivered'
                          ? 'bg-emerald-100 text-emerald-600'
                          : 'bg-violet-100 text-violet-600'
                        : displayPrescription.status === 'pending'
                          ? 'bg-amber-100 text-amber-600'
                          : displayPrescription.status === 'verified'
                            ? 'bg-teal-100 text-teal-600'
                            : displayPrescription.status === 'sent_to_pharmacy'
                              ? 'bg-purple-100 text-purple-600'
                              : displayPrescription.status === 'shipped'
                                ? 'bg-sky-100 text-sky-600'
                                : 'bg-emerald-100 text-emerald-600'
                    }`}
                  >
                    {displayPrescription.is_external_fulfillment && displayPrescription.status !== 'pending' && displayPrescription.status !== 'verified' ? (
                      displayPrescription.external_fulfillment_status === 'shipped' ? (
                        <Truck className='h-5 w-5' />
                      ) : displayPrescription.external_fulfillment_status === 'delivered' ? (
                        <PackageCheck className='h-5 w-5' />
                      ) : displayPrescription.external_fulfillment_status === 'sent' ? (
                        <Send className='h-5 w-5' />
                      ) : (
                        <Package className='h-5 w-5' />
                      )
                    ) : displayPrescription.status === 'pending' ? (
                      <Clock className='h-5 w-5' />
                    ) : displayPrescription.status === 'verified' ? (
                      <CheckCircle className='h-5 w-5' />
                    ) : displayPrescription.status === 'sent_to_pharmacy' ? (
                      <Send className='h-5 w-5' />
                    ) : displayPrescription.status === 'shipped' ? (
                      <Truck className='h-5 w-5' />
                    ) : (
                      <PackageCheck className='h-5 w-5' />
                    )}
                  </div>
                  <div className='flex-1 min-w-0'>
                    <div className='flex flex-wrap items-center gap-2'>
                      {displayPrescription.is_external_fulfillment && displayPrescription.status !== 'pending' && displayPrescription.status !== 'verified' ? (
                        <Badge className={`text-white border-0 text-xs ${
                          displayPrescription.external_fulfillment_status === 'sent'
                            ? 'bg-teal-600 hover:bg-teal-600'
                            : displayPrescription.external_fulfillment_status === 'shipped'
                            ? 'bg-sky-600 hover:bg-sky-600'
                            : displayPrescription.external_fulfillment_status === 'delivered'
                            ? 'bg-emerald-600 hover:bg-emerald-600'
                            : 'bg-violet-600 hover:bg-violet-600'
                        }`}>
                          {displayPrescription.external_fulfillment_status === 'sent'
                            ? 'Sent to External Pharmacy'
                            : displayPrescription.external_fulfillment_status === 'shipped'
                            ? 'Shipped (External)'
                            : displayPrescription.external_fulfillment_status === 'delivered'
                            ? 'Delivered (External)'
                            : 'Queued for External Fulfillment'}
                        </Badge>
                      ) : (
                        <Badge
                          className={`${getStatusColor(displayPrescription.status)} text-xs capitalize`}
                        >
                          {getStatusText(displayPrescription.status)}
                        </Badge>
                      )}
                      {displayPrescription.payment_status !== 'pending' && (
                        <Badge
                          className={`${getStatusColor(displayPrescription.payment_status)} text-xs capitalize`}
                        >
                          {getStatusText(displayPrescription.payment_status)}
                        </Badge>
                      )}
                    </div>
                    <p className='mt-1 break-words text-xs text-slate-400'>
                      Last updated:{' '}
                      {formatToLocalDate(displayPrescription.updated_at, 'MM/DD/YYYY hh:mm a')}
                    </p>
                    {(latestLink?.created_at || latestLink?.viewed_at) && (
                      <div className='mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500'>
                        {latestLink?.created_at && (
                          <div className='flex items-center gap-2'>
                            <Badge className='bg-white text-foreground hover:bg-white border border-border'>
                              Payment link sent
                            </Badge>
                            <span>
                              {formatToLocalDate(latestLink.created_at, 'MM/DD/YYYY hh:mm a')}
                            </span>
                          </div>
                        )}
                        {latestLink?.viewed_at && (
                          <div className='flex items-center gap-2'>
                            <Badge className='bg-emerald-100 text-emerald-700 hover:bg-emerald-100'>
                              Payment link viewed
                            </Badge>
                            <span>
                              {formatToLocalDate(latestLink.viewed_at, 'MM/DD/YYYY hh:mm a')}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                    {displayPrescription.status !== 'pending' && displayPrescription.status !== 'verified' && (
                      <p className='break-words text-xs text-slate-400'>
                        {displayPrescription.is_external_fulfillment
                          ? 'Routed to external pharmacy for fulfillment'
                          : 'Status updates are managed by pharmacy'}
                      </p>
                    )}
                  </div>

                  <div className='flex items-center gap-2'>
                    {/* Edit Prescription Button */}
                    {(['pending', 'verified'].includes(displayPrescription.status)) && displayPrescription.payment_status !== 'paid' && isPractitioner && (
                      <Button
                        variant='outline'
                        onClick={() => setIsEditPrescriptionOpen(true)}
                        className='h-9 shrink-0 rounded-lg px-4'
                      >
                        <Pencil className='h-4 w-4' />
                        Edit Prescription
                      </Button>
                    )}

                    {/* Cancel Prescription Button - hidden for guided order prescriptions */}
                    {(displayPrescription.status === 'pending' ||
                      (displayPrescription.status === 'verified' && displayPrescription.payment_status === 'paid')) &&
                      displayPrescription.status !== 'shipped' &&
                      displayPrescription.status !== 'delivered' &&
                      displayPrescription.status !== 'cancelled' &&
                      (!displayPrescription.cancellationRequests ||
                        displayPrescription.cancellationRequests.length === 0) &&
                      !(displayPrescription as any).guided_order_id &&
                      (isPractitioner || isAdmin) && (
                        <Button
                          variant='outline'
                          onClick={() => setIsCancelDialogOpen(true)}
                          className='h-9 shrink-0 rounded-lg border-red-200 px-4 text-red-600 hover:bg-red-50 hover:text-red-700'
                        >
                          <AlertCircle className='h-4 w-4' />
                          Cancel Prescription
                        </Button>
                      )}
                  </div>
                </div>
              </div>
            </div>

            {/* Guided Order (when linked) */}
            {guidedOrder && (
              <div className='space-y-4'>
                <h3 className='text-[0.8rem] font-semibold uppercase text-[#60378B]'>
                  Guided Order
                </h3>
                <div className='rounded-lg border border-slate-200 p-4 space-y-3'>
                  <OrderLifecycleTracker
                    status={guidedOrder.status}
                    paymentStatus={guidedOrder.payment_status}
                    holdExpiresAt={guidedOrder.hold_expires_at}
                    compact
                  />
                  {guidedOrder.payment_status === 'hold_placed' ? (
                    guidedOrder.hold_expires_at && (() => {
                      const days = Math.ceil(
                        (new Date(guidedOrder.hold_expires_at).getTime() - Date.now()) /
                          (1000 * 60 * 60 * 24)
                      );
                      return (
                        <p className='text-xs text-slate-600'>
                          Hold placed — {days <= 0 ? 'expired' : `expires in ${days} day${days === 1 ? '' : 's'}`}
                        </p>
                      );
                    })()
                  ) : (
                    (guidedOrder.payment_status === 'captured') && (
                      <p className='text-xs text-slate-600'>Payment captured</p>
                    )
                  )}
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() => {
                      setGuidedOrderIdToView(guidedOrder.id);
                      setGuidedOrderDetailOpen(true);
                    }}
                  >
                    View full order
                  </Button>
                </div>
              </div>
            )}

            {/* Cancellation Request Status */}
            {displayPrescription.cancellationRequests && displayPrescription.cancellationRequests.length > 0 && (
              <div className='space-y-4'>
                <h3 className='text-[0.8rem] font-semibold uppercase text-[#60378B]'>
                  Cancellation Request
                </h3>
                {(() => {
                  const latestRequest = displayPrescription.cancellationRequests[0];
                  return (
                    <div
                      className={`rounded-lg border p-4 min-w-0 overflow-hidden ${
                        latestRequest.status === 'rejected'
                          ? 'border-red-200 bg-red-50'
                          : latestRequest.status === 'approved'
                            ? 'border-green-200 bg-green-50'
                            : 'border-amber-200 bg-amber-50'
                      }`}
                    >
                      <div className='flex items-start gap-3 min-w-0'>
                        <div
                          className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${
                            latestRequest.status === 'rejected'
                              ? 'bg-red-100 text-red-600'
                              : latestRequest.status === 'approved'
                                ? 'bg-green-100 text-green-600'
                                : 'bg-amber-100 text-amber-600'
                          }`}
                        >
                          {latestRequest.status === 'rejected' ? (
                            <XCircle className='h-5 w-5' />
                          ) : latestRequest.status === 'approved' ? (
                            <CheckCircle className='h-5 w-5' />
                          ) : (
                            <Clock className='h-5 w-5' />
                          )}
                        </div>
                        <div className='flex-1 min-w-0'>
                          <div className='mb-2 flex items-center gap-2 flex-wrap'>
                            <Badge
                              className={`text-xs flex-shrink-0 ${
                                latestRequest.status === 'rejected'
                                  ? 'border border-red-200 bg-red-100 text-red-700'
                                  : latestRequest.status === 'approved'
                                    ? 'border border-green-200 bg-green-100 text-green-700'
                                    : 'border border-amber-200 bg-amber-100 text-amber-700'
                              }`}
                            >
                              {latestRequest.status === 'rejected'
                                ? 'Cancellation Rejected'
                                : latestRequest.status === 'approved'
                                  ? 'Cancellation Approved'
                                  : 'Cancellation Pending Review'}
                            </Badge>
                          </div>

                          <div className='space-y-2 text-sm min-w-0'>
                            <div className='min-w-0'>
                              <span className='font-medium text-slate-600'>Request Reason:</span>
                              <p className='mt-0.5 break-words text-slate-800'>{latestRequest.reason}</p>
                            </div>

                            <div className='text-xs text-slate-500'>
                              Requested on{' '}
                              {formatToLocalDate(
                                latestRequest.requested_at,
                                'MMM DD, YYYY [at] hh:mm A'
                              )}
                            </div>

                            {latestRequest.status === 'rejected' &&
                              latestRequest.rejection_reason && (
                                <div className='mt-3 rounded-md border border-red-200 bg-red-100 p-3 min-w-0'>
                                  <div className='flex items-start gap-2 min-w-0'>
                                    <Ban className='mt-0.5 h-4 w-4 flex-shrink-0 text-red-600' />
                                    <div className='flex-1 min-w-0'>
                                      <span className='break-words text-sm font-semibold text-red-700'>
                                        Rejection Reason from Super Admin:
                                      </span>
                                      <p className='mt-1 break-words text-sm text-red-800'>
                                        {latestRequest.rejection_reason}
                                      </p>
                                    </div>
                                  </div>
                                  {latestRequest.reviewed_at && (
                                    <p className='mt-2 text-xs text-red-600'>
                                      Rejected on{' '}
                                      {formatToLocalDate(
                                        latestRequest.reviewed_at,
                                        'MMM DD, YYYY [at] hh:mm A'
                                      )}
                                    </p>
                                  )}
                                </div>
                              )}

                            {latestRequest.status === 'approved' && latestRequest.reviewed_at && (
                              <div className='mt-2 text-xs text-green-600'>
                                Approved on{' '}
                                {formatToLocalDate(
                                  latestRequest.reviewed_at,
                                  'MMM DD, YYYY [at] hh:mm A'
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Prescription Information */}
            <div className='space-y-4'>
              <h3 className='text-[0.8rem] font-semibold uppercase text-[#60378B]'>
                Prescription Information
              </h3>
              <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
                <div className='flex items-center space-x-3 rounded-lg border border-slate-200 p-3 min-w-0'>
                  <div className='flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-slate-100'>
                    <User className='h-4 w-4 text-slate-600' />
                  </div>
                  <div className='min-w-0 flex-1'>
                    <p className='text-xs font-medium text-slate-400'>Patient</p>
                    <p className='break-words text-sm font-medium capitalize text-slate-900'>
                      {displayPrescription.patient?.full_name ?? '--'}
                    </p>
                  </div>
                </div>
                <div className='flex items-center space-x-3 rounded-lg border border-slate-200 p-3 min-w-0'>
                  <div className='flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-slate-100'>
                    <User className='h-4 w-4 text-slate-600' />
                  </div>
                  <div className='min-w-0 flex-1'>
                    <p className='text-xs font-medium text-slate-400'>Practitioner</p>
                    <p className='break-words text-sm font-medium capitalize text-slate-900'>
                      {displayPrescription.practitioner?.full_name ?? '--'}
                    </p>
                  </div>
                </div>
                <div className='flex items-center space-x-3 rounded-lg border border-slate-200 p-3'>
                  <div className='flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100'>
                    <Calendar className='h-4 w-4 text-slate-600' />
                  </div>
                  <div>
                    <p className='text-xs font-medium text-slate-400'>Date Created</p>
                    <p className='text-sm font-medium text-slate-900'>
                      {formatToLocalDate(displayPrescription.created_at, 'MM/DD/YYYY hh:mm a')}
                    </p>
                  </div>
                </div>
                <div className='flex items-center space-x-3 rounded-lg border border-slate-200 p-3'>
                  <div
                    className={`flex h-9 w-9 items-center justify-center rounded-lg ${getPriorityColor(displayPrescription.priority ?? 1)}`}
                  >
                    <Flag className='h-4 w-4' />
                  </div>
                  <div>
                    <p className='text-xs font-medium text-slate-400'>Priority</p>
                    <p className='text-sm font-medium text-slate-900'>
                      {getPriorityText(displayPrescription.priority ?? 1)}
                    </p>
                  </div>
                </div>
                <div className='flex items-center space-x-3 rounded-lg border border-slate-200 p-3'>
                  <div className='flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-100'>
                    <DollarSign className='h-4 w-4 text-emerald-600' />
                  </div>
                  <div>
                    <p className='text-xs font-medium text-slate-400'>Order Amount</p>
                    {totalDiscountCents > 0 ? (
                      <>
                        <p className='text-[11px] text-slate-400 line-through'>
                          ${(subtotalBeforeDiscountCents / 100).toFixed(2)}
                        </p>
                        <p className='text-sm font-medium text-slate-900'>
                          {prescriptionTotalAmountCents != null && prescriptionTotalAmountCents > 0
                            ? `$${(prescriptionTotalAmountCents / 100).toFixed(2)}`
                            : '--'}
                        </p>
                        <p className='text-[10px] text-emerald-600'>
                          Discount: -${(totalDiscountCents / 100).toFixed(2)}
                        </p>
                      </>
                    ) : (
                      <p className='text-sm font-medium text-slate-900'>
                        {prescriptionTotalAmountCents != null && prescriptionTotalAmountCents > 0
                          ? `$${(prescriptionTotalAmountCents / 100).toFixed(2)}`
                          : '--'}
                      </p>
                    )}
                    <p className='text-[10px] text-slate-400'>Excl. shipping</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Shipping Information - Only show if shipping is configured */}
            {displayPrescription.shipping_carrier_id && (
              <div className='space-y-4'>
                <h3 className='text-[0.8rem] font-semibold uppercase text-[#60378B]'>
                  Shipping Information
                </h3>
                <div className='rounded-lg border border-slate-200 p-4'>
                  {/* Free Shipping Badge */}
                  {displayPrescription.is_free_shipment && (
                    <div className='mb-4 flex items-center gap-2 rounded-lg bg-green-50 p-3 border border-green-200'>
                      <Gift className='h-5 w-5 text-green-600' />
                      <div>
                        <p className='text-sm font-medium text-green-800'>Free Shipping Applied</p>
                        <p className='text-xs text-green-600'>
                          {displayPrescription.free_shipment_type === 'global' 
                            ? 'Applies to all clinics' 
                            : 'Clinic-specific promotion'}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
                    {/* Carrier */}
                    <div className='flex items-center space-x-3 rounded-lg border border-slate-200 p-3'>
                      <div className='flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100'>
                        <Truck className='h-4 w-4 text-slate-600' />
                      </div>
                      <div>
                        <p className='text-xs font-medium text-slate-400'>Carrier</p>
                        <p className='text-sm font-medium text-slate-900'>
                          {displayPrescription.shipping_carrier_name || '--'}
                        </p>
                      </div>
                    </div>
                    
                    {/* Service */}
                    <div className='flex items-center space-x-3 rounded-lg border border-slate-200 p-3'>
                      <div className='flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100'>
                        <Package className='h-4 w-4 text-slate-600' />
                      </div>
                      <div>
                        <p className='text-xs font-medium text-slate-400'>Service</p>
                        <p className='text-sm font-medium text-slate-900'>
                          {displayPrescription.shipping_service_name || '--'}
                        </p>
                      </div>
                    </div>
                    
                    {/* Shipping Cost */}
                    <div className='flex items-center space-x-3 rounded-lg border border-slate-200 p-3'>
                      <div className='flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100'>
                        <DollarSign className='h-4 w-4 text-slate-600' />
                      </div>
                      <div>
                        <p className='text-xs font-medium text-slate-400'>Shipping Cost</p>
                        <div className='flex items-center gap-2'>
                          {displayPrescription.is_free_shipment ? (
                            <>
                              <span className='text-xs text-slate-400 line-through'>
                                ${((displayPrescription.shipping_original_cost_cents || 0) / 100).toFixed(2)}
                              </span>
                              <span className='text-sm font-semibold text-green-600'>$0.00</span>
                            </>
                          ) : (
                            <span className='text-sm font-medium text-slate-900'>
                              ${((displayPrescription.shipping_cost_cents || 0) / 100).toFixed(2)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Follow-up Lab Information - Only show if follow-up lab is enabled */}
            {displayPrescription.order_followup_lab && (
              <div className='space-y-4'>
                <h3 className='text-[0.8rem] font-semibold uppercase text-[#60378B]'>
                  Follow-up Lab
                </h3>
                <div className='rounded-lg border border-purple-200 bg-purple-50 p-4'>
                  <div className='flex items-start space-x-3'>
                    <div className='flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-purple-100'>
                      <FlaskConical className='h-4 w-4 text-purple-600' />
                    </div>
                    <div className='flex-1 min-w-0'>
                      <div className='mb-3 flex items-center gap-2'>
                        <Badge className='border border-purple-200 bg-purple-100 text-xs text-purple-700'>
                          Follow-up Lab Enabled
                        </Badge>
                      </div>
                      <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
                        {/* Follow-up Days */}
                        {displayPrescription.followup_days && (
                          <div className='flex items-center space-x-3 rounded-lg border border-purple-200 bg-white p-3'>
                            <div className='flex h-9 w-9 items-center justify-center rounded-lg bg-purple-100'>
                              <CalendarDays className='h-4 w-4 text-purple-600' />
                            </div>
                            <div>
                              <p className='text-xs font-medium text-slate-400'>Follow-up After</p>
                              <p className='text-sm font-medium text-slate-900'>
                                {displayPrescription.followup_days} {displayPrescription.followup_days === 1 ? 'day' : 'days'}
                              </p>
                            </div>
                          </div>
                        )}
                        
                        {/* Junction Test ID */}
                        {displayPrescription.followup_junction_test_id && (
                          <div className='flex items-center space-x-3 rounded-lg border border-purple-200 bg-white p-3 min-w-0'>
                            <div className='flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-purple-100'>
                              <Hash className='h-4 w-4 text-purple-600' />
                            </div>
                            <div className='min-w-0 flex-1'>
                          <p className='text-xs font-medium text-slate-400'>Lab Test ID</p>
                              <p className='break-words text-sm font-medium text-slate-900'>
                                {displayPrescription.followup_junction_test_id}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Medications */}
            <div className='space-y-4'>
              <h3 className='text-[0.8rem] font-semibold uppercase text-[#60378B]'>Medications</h3>
              <Accordion
                type='single'
                collapsible
                defaultValue='medication-0'
                className='space-y-3'
              >
                {displayPrescription.medicationRequests?.map((medication, medIndex) => {
                  const reasonForCompounding = JSON.parse(
                    medication?.reason_for_compounding || '{}'
                  );
                  const clinicProgram = (medication as { clinicProgram?: { title?: string; subtitle?: string; inclusions?: string; target?: string } }).clinicProgram;
                  return (
                    <AccordionItem
                      key={medication.id || medIndex}
                      value={`medication-${medIndex}`}
                      className='overflow-hidden rounded-lg border border-slate-200'
                    >
                      <AccordionTrigger className='px-4 py-3 hover:bg-slate-50 hover:no-underline'>
                        <div className='flex items-center gap-3 w-full min-w-0'>
                          <div className='flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-slate-100'>
                            <Pill className='h-4 w-4 text-slate-600' />
                          </div>
                          <div className='text-left min-w-0 flex-1'>
                            <div className='flex items-center gap-2 flex-wrap'>
                              <p className='break-words text-sm font-medium text-slate-900'>
                                {medication.medication}
                              </p>
                              {medication.rx_number && (
                                <Badge
                                  variant='outline'
                                  className='border-blue-200 bg-blue-50 text-xs text-blue-700 flex-shrink-0'
                                >
                                  RX: {medication.rx_number}
                                </Badge>
                              )}
                            </div>
                            <p className='break-words text-xs text-slate-400'>
                              {medication.dose} • Qty: {medication.quantity_authorized}
                            </p>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className='px-4'>
                        <div className='grid grid-cols-1 gap-3 pt-2 md:grid-cols-2'>
                          {/* Dosage */}
                          <div className='flex items-center space-x-3 rounded-lg border border-slate-200 p-3'>
                            <div className='flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100'>
                              <Syringe className='h-4 w-4 text-slate-600' />
                            </div>
                            <div>
                              <p className='text-xs font-medium text-slate-400'>Dosage</p>
                              <p className='text-sm font-medium text-slate-900'>
                                {medication.dose}
                              </p>
                            </div>
                          </div>

                          {/* RX Number */}
                          <div className='flex items-center space-x-3 rounded-lg border border-slate-200 p-3'>
                            <div className='flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100'>
                              <Hash className='h-4 w-4 text-slate-600' />
                            </div>
                            <div>
                              <p className='text-xs font-medium text-slate-400'>RX Number</p>
                              <p className='text-sm font-medium text-slate-900'>
                                {medication.rx_number || '--'}
                              </p>
                            </div>
                          </div>

                          {/* Refills */}
                          <div className='flex items-center space-x-3 rounded-lg border border-slate-200 p-3'>
                            <div className='flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100'>
                              <RotateCcw className='h-4 w-4 text-slate-600' />
                            </div>
                            <div>
                              <p className='text-xs font-medium text-slate-400'>Refills</p>
                              <p className='text-sm font-medium text-slate-900'>
                                {medication.refills_authorized}
                              </p>
                            </div>
                          </div>

                          {/* Authorized Quantity */}
                          <div className='flex items-center space-x-3 rounded-lg border border-slate-200 p-3'>
                            <div className='flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100'>
                              <Hash className='h-4 w-4 text-slate-600' />
                            </div>
                            <div>
                              <p className='text-xs font-medium text-slate-400'>
                                Authorized Quantity
                              </p>
                              <p className='text-sm font-medium text-slate-900'>
                                {medication.quantity_authorized}
                              </p>
                            </div>
                          </div>

                          {/* Unit of Measure */}
                          <div className='flex items-center space-x-3 rounded-lg border border-slate-200 p-3'>
                            <div className='flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100'>
                              <Scale className='h-4 w-4 text-slate-600' />
                            </div>
                            <div>
                              <p className='text-xs font-medium text-slate-400'>Unit of Measure</p>
                              <p className='text-sm font-medium text-slate-900'>
                                {medicationUnits[
                                  medication.unit_of_measure as keyof typeof medicationUnits
                                ] || medication.unit_of_measure || '--'}
                              </p>
                            </div>
                          </div>

                          {/* Days Supply */}
                          <div className='flex items-center space-x-3 rounded-lg border border-slate-200 p-3'>
                            <div className='flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100'>
                              <CalendarDays className='h-4 w-4 text-slate-600' />
                            </div>
                            <div>
                              <p className='text-xs font-medium text-slate-400'>Days Supply</p>
                              <p className='text-sm font-medium text-slate-900'>
                                {medication.day_supply || '--'}
                              </p>
                            </div>
                          </div>

                          {/* Flavor */}
                          <div className='flex items-center space-x-3 rounded-lg border border-slate-200 p-3'>
                            <div className='flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100'>
                              <Palette className='h-4 w-4 text-slate-600' />
                            </div>
                            <div>
                              <p className='text-xs font-medium text-slate-400'>Flavor</p>
                              <p className='text-sm font-medium text-slate-900'>
                                {medication.flavor || '--'}
                              </p>
                            </div>
                          </div>

                          {/* Clinic Program - shown when medication is from a clinic program */}
                          {clinicProgram && (
                            <div className='flex items-start space-x-3 rounded-lg border border-violet-200 bg-violet-50/50 p-3 md:col-span-2 min-w-0'>
                              <div className='flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-violet-100'>
                                <Layers className='h-4 w-4 text-violet-600' />
                              </div>
                              <div className='flex-1 min-w-0'>
                                <p className='text-xs font-medium text-slate-400'>Clinic Program</p>
                                <p className='break-words text-sm font-medium text-slate-900'>
                                  {clinicProgram.title || '--'}
                                </p>
                                {clinicProgram.subtitle && (
                                  <p className='mt-1 break-words text-xs text-slate-600'>
                                    {clinicProgram.subtitle}
                                  </p>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Notes */}
                          <div className='flex items-center space-x-3 rounded-lg border border-slate-200 p-3 md:col-span-2 min-w-0'>
                            <div className='flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-slate-100'>
                              <FileText className='h-4 w-4 text-slate-600' />
                            </div>
                            <div className='flex-1 min-w-0'>
                              <p className='text-xs font-medium text-slate-400'>Notes</p>
                              <p className='break-words text-sm font-medium text-slate-900'>
                                {medication.notes || '--'}
                              </p>
                            </div>
                          </div>

                          {/* Instructions */}
                          <div className='flex items-center space-x-3 rounded-lg border border-slate-200 p-3 md:col-span-2 min-w-0'>
                            <div className='flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-slate-100'>
                              <MessageSquare className='h-4 w-4 text-slate-600' />
                            </div>
                            <div className='flex-1 min-w-0'>
                              <p className='text-xs font-medium text-slate-400'>Instructions</p>
                              <p className='break-words text-sm font-medium text-slate-900'>
                                {medication.sig || '--'}
                              </p>
                            </div>
                          </div>

                          {/* Reason For Compounding */}
                          {reasonForCompounding?.description && (
                            <div className='flex items-center space-x-3 rounded-lg border border-slate-200 p-3 md:col-span-2 min-w-0'>
                              <div className='flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-slate-100'>
                                <FlaskConical className='h-4 w-4 text-slate-600' />
                              </div>
                              <div className='flex-1 min-w-0'>
                                <p className='text-xs font-medium text-slate-400'>
                                  Reason For Compounding
                                </p>
                                <p className='break-words text-sm font-medium text-slate-900'>
                                  {reasonForCompounding?.description}
                                </p>
                              </div>
                            </div>
                          )}

                          {/* Additional Context */}
                          {reasonForCompounding?.context && (
                            <div className='flex items-center space-x-3 rounded-lg border border-slate-200 p-3 md:col-span-2 min-w-0'>
                              <div className='flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-slate-100'>
                                <Info className='h-4 w-4 text-slate-600' />
                              </div>
                              <div className='flex-1 min-w-0'>
                                <p className='text-xs font-medium text-slate-400'>
                                  Additional Context
                                </p>
                                <p className='break-words text-sm font-medium text-slate-900'>
                                  {reasonForCompounding?.context}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            </div>

            {/* Workflow Progress */}
            <div className='space-y-4'>
              <h3 className='text-[0.8rem] font-semibold uppercase text-[#60378B]'>
                Workflow Progress
              </h3>
              <div className='space-y-2'>
                {getWorkflowSteps(displayPrescription.status).map((step, index) => {
                  const Icon = step.icon;
                  return (
                    <div key={step.key} className='flex items-center space-x-3 py-1'>
                      <div
                        className={`flex h-7 w-7 items-center justify-center rounded-full ${
                          step.completed
                            ? 'bg-brand-teal/10 text-brand-teal'
                            : 'bg-slate-100 text-slate-300'
                        }`}
                      >
                        <Icon className='h-3.5 w-3.5' />
                      </div>
                      <div className='flex-1'>
                        <p
                          className={`text-sm ${step.completed ? 'font-medium text-slate-700' : 'text-slate-400'}`}
                        >
                          {step.label}
                        </p>
                      </div>
                      {step.current && (
                        <span className='rounded-full bg-brand-teal/10 px-2 py-0.5 text-xs font-medium text-brand-teal'>
                          Current
                        </span>
                      )}
                      {step.completed && !step.current && (
                        <CheckCircle className='h-4 w-4 text-brand-teal' />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Post-Payment Information */}
            {displayPrescription.status !== 'pending' && displayPrescription.status !== 'verified' && (
              <div className='space-y-4'>
                <h3 className='text-[0.8rem] font-semibold uppercase text-[#60378B]'>
                  Fulfillment Information
                </h3>
                <div className='rounded-lg border border-slate-200 bg-slate-50 p-4'>
                  <div className='flex items-start space-x-3'>
                    <div className='flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-slate-100'>
                      <Package className='h-4 w-4 text-slate-500' />
                    </div>
                    <div>
                      <h4 className='mb-1 text-sm font-medium text-slate-700'>
                        Prescription Fulfilled Externally
                      </h4>
                      <p className='text-xs text-slate-500'>
                        This prescription has been paid for and is now being handled by the
                        pharmacy. All status updates are automatically received from the pharmacy
                        system.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Audit Trail */}
            <div className='space-y-4'>
              <PrescriptionAuditTrail auditLogs={auditLogs} isLoading={isLoadingAuditTrail} />
            </div>
          </div>
        </div>

        {/* Footer - Quick Actions */}
        {(displayPrescription.status === 'pending' || displayPrescription.status === 'verified') && (
          <div className='flex items-center justify-between border-t border-slate-200 px-6 py-4'>
            <Button
              variant='ghost'
              type='button'
              className='font-medium text-slate-700 hover:bg-transparent hover:text-slate-900'
              onClick={() => onOpenChange(false)}
            >
              Close
            </Button>
            <div className='flex items-center gap-2'>
              {displayPrescription.status === 'pending' && (
                <>
                  {isPractitioner && (
                    <Button
                      onClick={handleVerifyClick}
                      disabled={isVerificationProcessing}
                      className='h-10 rounded-lg bg-brand-teal px-4 hover:bg-brand-teal/90'
                    >
                      {isVerificationProcessing ? (
                        <>
                          <Loader2 className='h-4 w-4 animate-spin' />
                          Verifying...
                        </>
                      ) : (
                        <>
                          <CheckCircle className='h-4 w-4' />
                          Verify
                        </>
                      )}
                    </Button>
                  )}
                </>
              )}

              {displayPrescription.status === 'verified' && displayPrescription.payment_status === 'paid' && (
                <>
                  <Button
                    onClick={handleSubmitToPharmacy}
                    disabled={isSubmittingToPharmacy}
                    className='h-10 rounded-lg bg-brand-copper px-4 hover:bg-brand-copper/90'
                  >
                    {isSubmittingToPharmacy ? (
                      <>
                        <Loader2 className='h-4 w-4 animate-spin' />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className='h-4 w-4' />
                        Submit to Pharmacy
                      </>
                    )}
                  </Button>
                </>
              )}

              {displayPrescription.payment_status === 'pending' &&
                !displayPrescription.cancellationRequests?.some(
                  request => request.status === 'pending'
                ) &&
                permissionCheck(
                  'payments:collect_payment',
                  rolePermissions,
                  isPractitioner || isAdmin
                ) && (
                  <>
                    <Button
                      variant='outline'
                      onClick={handleSendLinkClick}
                      disabled={isPaymentProcessing}
                      className='h-10 rounded-lg px-4'
                    >
                      <Send className='h-4 w-4' />
                      Send Payment Link
                    </Button>
                    <Button
                      onClick={handlePaymentClick}
                      disabled={isPaymentProcessing}
                      className='h-10 rounded-lg bg-brand-teal px-4 hover:bg-brand-teal/90'
                    >
                      {isPaymentProcessing ? (
                        <>
                          <Loader2 className='h-4 w-4 animate-spin' />
                          Processing...
                        </>
                      ) : (
                        <>
                          <CreditCard className='h-4 w-4' />
                          Process Payment
                        </>
                      )}
                    </Button>
                  </>
                )}
            </div>
          </div>
        )}
      </SheetContent>

      {/* Verification Confirmation Dialog */}
      {isVerificationConfirmationOpen && (
        <VerificationConfirmationDialog
          open={isVerificationConfirmationOpen}
          onOpenChange={setIsVerificationConfirmationOpen}
          prescriptionId={displayPrescription.id}
          patientName={displayPrescription.patient?.full_name}
          practitionerName={displayPrescription.practitioner?.full_name}
          onConfirm={async () => {
            try {
              setIsVerificationProcessing(true);
              await prescriptionService.verifyPrescription(displayPrescription.id);
              if (displayPrescription.payment_status === 'paid') {
                await prescriptionService.submitPrescription(displayPrescription.id);
              }
              onActionComplete?.();
              onStatusUpdate(displayPrescription.id, 'verified');
              toast({
                title: 'Success',
                description: 'Order verified successfully',
                variant: 'success',
              });
            } catch (error: any) {
              console.error('Error verifying prescription:', error);
              toast({
                title: 'Error',
                description: error.message || 'Failed to verify order',
                variant: 'destructive',
              });
              throw error;
            } finally {
              setIsVerificationProcessing(false);
            }
          }}
          isLoading={isVerificationProcessing}
        />
      )}

      {/* Payment Sheet */}
      {isPaymentSheetOpen && (
        <PaymentSheet
          open={isPaymentSheetOpen}
          onOpenChange={setIsPaymentSheetOpen}
          prescriptionId={displayPrescription.id}
          onPaymentComplete={async () => {
            try {
              setIsPaymentProcessing(true);
              if (displayPrescription.status === 'verified') {
                await prescriptionService.submitPrescription(displayPrescription.id);
              }
              onActionComplete?.();
              toast({
                title: 'Success',
                description: 'Payment processed and order placed successfully',
                variant: 'success',
              });
              setIsPaymentSheetOpen(false);
            } catch (error: any) {
              console.error('Error processing payment:', error);
              toast({
                title: 'Error',
                description: error.message || 'Failed to process payment',
                variant: 'destructive',
              });
              throw error;
            } finally {
              setIsPaymentProcessing(false);
            }
          }}
        />
      )}

      {/* Guided Order Detail Sheet */}
      <GuidedOrderDetailSheet
        orderId={guidedOrderIdToView}
        open={guidedOrderDetailOpen}
        onOpenChange={open => {
          setGuidedOrderDetailOpen(open);
          if (!open) setGuidedOrderIdToView(null);
        }}
      />

      {/* Send Payment Link Dialog */}
      {isSendLinkSheetOpen && (
        <SendPaymentLinkDialog
          open={isSendLinkSheetOpen}
          onOpenChange={setIsSendLinkSheetOpen}
          prescription_id={displayPrescription.id}
          onSuccess={() => {
            setIsSendLinkSheetOpen(false);
            onOpenChange(false);
            onActionComplete?.();
          }}
        />
      )}

      {/* Refund Dialog */}
      {transactionId && (
        <RefundDialog
          open={isRefundDialogOpen}
          onOpenChange={setIsRefundDialogOpen}
          transactionId={transactionId}
          transactionAmount={transactionAmount}
          prescriptionStatus={displayPrescription.status}
          onRefundSuccess={() => {
            setIsRefundDialogOpen(false);
            setTransactionId(null);
            setTransactionAmount(0);
            onActionComplete?.();
            // Refresh prescription data
            if (displayPrescription.id) {
              // Trigger a refresh by calling onStatusUpdate or onActionComplete
              toast({
                title: 'Refund Processed',
                description:
                  'The refund has been processed successfully. Prescription payment status has been updated.',
                variant: 'default',
              });
            }
          }}
        />
      )}

      {/* Cancel Prescription Dialog */}
      <CancelPrescriptionDialog
        open={isCancelDialogOpen}
        onOpenChange={setIsCancelDialogOpen}
        prescription={displayPrescription}
        onSuccess={() => {
          setIsCancelDialogOpen(false);
          onActionComplete?.();
        }}
      />

      {/* Edit Medications Dialog */}
      <EditMedicationsDialog
        open={isEditMedicationsOpen}
        onOpenChange={setIsEditMedicationsOpen}
        prescription={displayPrescription}
        onMedicationsUpdated={() => {
          fetchFullPrescription();
          onActionComplete?.();
        }}
      />

      <PrescriptionFormSheet
        open={isEditPrescriptionOpen}
        onOpenChange={setIsEditPrescriptionOpen}
        mode="edit"
        initialData={displayPrescription}
        patientId={displayPrescription.patient?.id || ''}
        practitionerId={displayPrescription.practitioner?.id || ''}
        onSubmit={() => {
          fetchFullPrescription();
          onActionComplete?.();
        }}
      />
    </Sheet>
  );
};

export default PrescriptionDetailSheet;
